import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../modals/Modal';
import Spinner from '../../Spinner';
import SubscriptionStatusBadge from './SubscriptionStatusBadge';
import { fetchUserSubscriptionDetails } from '../../../services/admin/subscriptionTrackerApi';
import {
  SportSubscriptionDetail,
  TeamSportSubscriptionDetail,
  UserSubscriptionDetailResponse,
} from '../../../types/admin/subscriptionTracker';
import {
  formatDaysRemaining,
  formatSubscriptionDate,
  getSportLabel,
} from '../../../utils/admin/formatSubscriptionDate';

const TRIAL_USER_STATUSES = new Set(['trial', 'trial_end']);

interface UserViewPlanModalProps {
  isOpen: boolean;
  userId: number | null;
  username: string;
  email?: string;
  teamId?: number | null;
  allowAll?: boolean;
  /** 'user' = User Sub tab layout (team sub when no user trials). Default 'all'. */
  mode?: 'all' | 'user';
  onClose: () => void;
}

function emptyCell(value: string | null | undefined): string {
  const trimmed = value?.trim();
  return trimmed ? trimmed : '—';
}

function PlanSportTable({
  rows,
  variant,
}: {
  rows: Array<{
    sport_code: string;
    statusLabel: string;
    start_date: string | null;
    end_date: string | null;
    days_remaining: number | null;
  }>;
  variant: 'user' | 'team';
}) {
  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sport</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">End</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {rows.map((row) => (
            <tr key={`${variant}-${row.sport_code}`}>
              <td className="px-3 py-2 font-medium">{getSportLabel(row.sport_code)}</td>
              <td className="px-3 py-2">
                {row.statusLabel ? (
                  <SubscriptionStatusBadge status={row.statusLabel} />
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-600">{formatSubscriptionDate(row.start_date)}</td>
              <td className="px-3 py-2 text-gray-600">{formatSubscriptionDate(row.end_date)}</td>
              <td className="px-3 py-2 text-gray-600">
                {row.days_remaining != null ? formatDaysRemaining(row.days_remaining) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function userHasTrialSports(sports: SportSubscriptionDetail[]): boolean {
  return sports.some((s) => TRIAL_USER_STATUSES.has(s.status?.toLowerCase() ?? ''));
}

function teamHasSubscriptions(sports: TeamSportSubscriptionDetail[]): boolean {
  const allowed = new Set([
    'trial',
    'grace',
    'subscribed',
    'grace_ended',
    'subscription_end',
  ]);
  return sports.some((s) => {
    const display = (s.display_status || s.status || '').toLowerCase();
    return allowed.has(display);
  });
}

function mapUserSportRows(sports: SportSubscriptionDetail[]) {
  return sports.map((sport) => ({
    sport_code: sport.sport_code,
    statusLabel: emptyCell(sport.status) === '—' ? '' : sport.status,
    start_date: sport.start_date,
    end_date: sport.end_date,
    days_remaining: sport.days_remaining,
  }));
}

function mapTeamSportRows(sports: TeamSportSubscriptionDetail[]) {
  return sports.map((sport) => {
    const displayStatus =
      sport.display_status ||
      (sport.status === 'subscribed' && sport.is_grace ? 'grace' : sport.status);
    return {
      sport_code: sport.sport_code,
      statusLabel: displayStatus,
      start_date: sport.start_date,
      end_date: sport.end_date,
      days_remaining: sport.days_remaining,
    };
  });
}

const UserViewPlanModal: React.FC<UserViewPlanModalProps> = ({
  isOpen,
  userId,
  username,
  email = '',
  teamId = null,
  allowAll = false,
  mode = 'all',
  onClose,
}) => {
  const [data, setData] = useState<UserSubscriptionDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || userId == null) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (allowAll && teamId == null) {
      return;
    }

    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      const result = await fetchUserSubscriptionDetails(userId, {
        ...(allowAll && teamId != null ? { allow_all: true, team_id: teamId } : {}),
        includeActivatedBy: mode === 'all',
        signal: controller.signal,
      });

      if (result.aborted || controller.signal.aborted) return;

      if (result.status === 404) {
        setError('User not found');
      } else if (result.data) {
        setData(result.data);
      } else {
        setError(result.error ?? 'Failed to load plan details');
      }

      setLoading(false);
    };

    void load();

    return () => {
      controller.abort();
    };
  }, [isOpen, userId, teamId, allowAll, mode]);

  const displayUsername = data?.username || username;
  const displayEmail = data?.email || email;

  const userSports = data?.user_sports ?? [];
  const teamSports = data?.team_sports ?? [];

  const hasUserTrials = useMemo(() => userHasTrialSports(userSports), [userSports]);
  const hasTeamSubs = useMemo(() => teamHasSubscriptions(teamSports), [teamSports]);

  const showUserSection = hasUserTrials;
  const showTeamSection = hasTeamSubs;

  const userRows = useMemo(() => mapUserSportRows(userSports), [userSports]);
  const teamRows = useMemo(() => mapTeamSportRows(teamSports), [teamSports]);

  const nothingToShow = !showUserSection && !showTeamSection;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      closeOnBackdrop
      closeOnEsc
      panelClassName="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col relative mx-4"
      backdropClassName="fixed inset-0 flex items-center justify-center bg-black/50 z-50 px-4"
    >
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mr-8">
          View Plan — {displayUsername}
          {displayEmail ? ` · ${displayEmail}` : ''}
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {loading && (
          <div className="flex justify-center items-center h-32">
            <Spinner size="md" />
            <span className="ml-2 text-gray-600">Loading plan...</span>
          </div>
        )}

        {error && !loading && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
        )}

        {data && !loading && !error && (
          <>
            <p className="text-sm font-semibold text-gray-900">{data.team_name}:</p>

            {nothingToShow && (
              <p className="text-sm text-gray-500 py-2">No subscriptions for this user.</p>
            )}

            {showUserSection && (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  User subscription
                </h3>
                <PlanSportTable rows={userRows} variant="user" />
              </section>
            )}

            {showTeamSection && teamRows.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Team subscription
                </h3>
                <PlanSportTable rows={teamRows} variant="team" />
              </section>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default UserViewPlanModal;
